import React, {useCallback, useEffect, useState} from "react";
import AppContainer from "../../common/app_container";
import DataTable from "react-data-table-component";
import {useNavigate} from "react-router-dom";
import Alert, {AlertProps} from "../../common/alert";
import _ from "lodash";
import {CreatorApplication} from "@src/model/creator_application";
import {useCreatorApplicationAPI} from "@src/api/creator_application_api";

const CreatorApplicationIndex: React.FC = () => {
    const [data, setData] = useState<CreatorApplication[]>([]);
    const [search, setSearch] = useState("");
    const [searchStatus, setSearchStatus] = useState("");
    const [status, setStatus] = useState<AlertProps | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [totalRows, setTotalRows] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const {findApplications} = useCreatorApplicationAPI();
    const navigate = useNavigate();

    const getApplicantName = (row: CreatorApplication): string => {
        return [row.first_name, row.last_name].filter(Boolean).join(" ") || "N/A";
    };

    // Normalize status to handle bad data (e.g., REAL_ESTATE -> real_estate)
    const normalizeStatus = (status: string): string => {
        if (!status) return status;
        // Convert to lowercase, but preserve common status formats
        const upperStatus = status.toUpperCase();
        if (upperStatus === "PENDING" || upperStatus === "PAYMENT_INITIATED" ||
            upperStatus === "APPROVED" || upperStatus === "REJECTED") {
            return upperStatus;
        }
        // For other statuses (like REAL_ESTATE), convert to lowercase
        return status.toLowerCase();
    };

    const columns = [
        {
            name: "Applicant Name",
            selector: getApplicantName,
            sortable: true,
        },
        {
            name: "Email",
            selector: (row: CreatorApplication) => row.email,
            sortable: true,
        },
        {
            name: "Status",
            selector: (row: CreatorApplication) => normalizeStatus(row.status),
            sortable: true,
            cell: (row: CreatorApplication) => {
                const normalizedStatus = normalizeStatus(row.status);
                const upperStatus = normalizedStatus.toUpperCase();
                return (
                    <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${upperStatus === "APPROVED"
                            ? "bg-green-100 text-green-700"
                            : upperStatus === "PAYMENT_INITIATED"
                                ? "bg-yellow-100 text-yellow-700"
                                : upperStatus === "REJECTED"
                                    ? "bg-red-100 text-red-700"
                                    : "bg-blue-100 text-blue-700"
                        }`}
                    >
                        {normalizedStatus}
                    </span>
                );
            },
        },
        {
            name: "Submitted At",
            selector: (row: CreatorApplication) =>
                row.created_at ? new Date(row.created_at).toLocaleDateString() : "N/A",
            sortable: true,
        },
        {
            name: "Actions",
            cell: (row: CreatorApplication) => (
                <button
                    onClick={() => navigate(`/creator-applications/review/${row.id}`)}
                    className="px-3 py-1 bg-blue-500 text-white text-xs font-bold rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                >
                    Review
                </button>
            ),
        },
    ];

    const fetchApplications = useCallback(
        async (searchTerm: string, statusFilter: string, page: number, count: number) => {
            setLoading(true);
            setStatus(null);
            try {
                const offset = (page - 1) * count;
                const response = await findApplications({q: searchTerm, status: statusFilter, limit: count, offset});
                setData(response);
                if (offset === 0 && response.length < count) {
                    setTotalRows(response.length);
                } else if (response.length === 0 && page > 1) {
                    setTotalRows(offset);
                } else {
                    setTotalRows(offset + response.length + (response.length === count ? 1 : 0));
                }
            } catch (error: any) {
                setStatus({type: "error", message: "Failed to load applications: " + error.message});
            } finally {
                setLoading(false);
            }
        },
        [findApplications]
    );

    const debouncedSearch = useCallback(
        _.debounce((term: string, statusFilter: string) => {
            setCurrentPage(1);
            fetchApplications(term, statusFilter, 1, perPage);
        }, 500), // 500ms debounce timeout
        [fetchApplications, perPage]
    );

    useEffect(() => {
        fetchApplications(search, searchStatus, currentPage, perPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const term = event.target.value;
        setSearch(term);
        debouncedSearch(term, searchStatus);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchApplications(search, searchStatus, page, perPage);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
        fetchApplications(search, searchStatus, page, newPerPage);
    };

    return (
        <AppContainer>
            <div className="md:p-6">
                <div className="flex justify-between items-center mb-4">
                    <h1 className="text-2xl font-semibold">Creator Applications</h1>
                </div>

                {status && (
                    <div className="mb-4">
                        <Alert {...status} onClose={() => setStatus(null)}/>
                    </div>
                )}
                <div className="mb-4 flex flex-row gap-4 justify-end">
                    <div className="relative w-full max-w-xs">
                        <label htmlFor="application-search" className="sr-only">
                            Search Applications
                        </label>
                        <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                            <svg
                                className="w-4 h-4 text-gray-400"
                                aria-hidden="true"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    stroke="currentColor"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                                />
                            </svg>
                        </div>
                        <input
                            type="search"
                            id="application-search"
                            value={search}
                            onChange={handleSearchChange}
                            className="block w-full p-2 ps-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Search Name, Email, Status..."
                        />
                    </div>

                    {/* Status Dropdown */}
                    <div className="relative w-full max-w-xs">
                        <label htmlFor="status-filter" className="sr-only">
                            Filter by Status
                        </label>
                        <select
                            id="status-filter"
                            value={searchStatus}
                            onChange={(e) => {
                                setSearchStatus(e.target.value);
                                debouncedSearch(search, e.target.value);
                            }}
                            className="block w-full p-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">All Statuses</option>
                            <option value="PENDING">Pending</option>
                            <option value="PAYMENT_INITIATED">Payment Initiated</option>
                            <option value="APPROVED">Approved</option>
                            <option value="REJECTED">Rejected</option>
                        </select>
                    </div>
                </div>

                <div className="border rounded shadow-sm">
                    <DataTable
                        columns={columns}
                        data={data}
                        progressPending={loading}
                        progressComponent={<div className="py-10 text-center">Loading...</div>}
                        noDataComponent={<div className="py-10 text-center text-gray-500">No applications found.</div>}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        onChangeRowsPerPage={handlePerRowsChange}
                        onChangePage={handlePageChange}
                        paginationDefaultPage={currentPage}
                        paginationPerPage={perPage}
                        paginationRowsPerPageOptions={[10, 20, 50, 100]}
                        highlightOnHover
                        striped
                        onRowClicked={(row) => navigate(`/creator-applications/review/${row.id}`)}
                        pointerOnHover
                        customStyles={{
                            headCells: {
                                style: {
                                    fontSize: "13px",
                                    fontWeight: "bold",
                                    color: "#4a5568",
                                    backgroundColor: "#f7fafc"
                                },
                            },
                            cells: {
                                style: {fontSize: "13px", cursor: "pointer"}
                            },
                        }}
                    />
                </div>
            </div>
        </AppContainer>
    );
};

export default CreatorApplicationIndex;

